import { useState, useEffect, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { Plus, User, Search, Phone, Mail, ArrowUpDown, ArrowUp, ArrowDown, Filter, ChevronDown, ChevronUp, X, MapPin } from 'lucide-react'
import toast from 'react-hot-toast'
import { supabase } from '../../lib/supabase'

interface CustomerWithCount {
  id: string
  fullName: string
  email: string
  phone: string
  addressStreet: string
  addressCity: string
  addressPostalCode: string
  addressCountry: string
  notes?: string
  active: boolean
  createdAt: string
  updatedAt: string
  assignmentCount: number
  provisionPartnerId: string | null
  fortnoxCustomerId: string | null
  wantsRut: boolean
}

interface ProvisionPartner {
  id: string
  name: string
}

const CUSTOMERS_FILTER_STORAGE_KEY = 'customers-advanced-filters'

interface CustomerFilters {
  showAdvancedFilters: boolean
  selectedStatuses: string[]
  selectedCities: string[]
  selectedPostalCodes: string[]
  selectedProvisionPartners: string[]
  selectedRut: string[]
  itemsPerPage: number
}

const getSavedFilters = (): CustomerFilters | null => {
  try {
    const saved = sessionStorage.getItem(CUSTOMERS_FILTER_STORAGE_KEY)
    if (saved) {
      return JSON.parse(saved)
    }
  } catch (e) {
    console.error('Error loading saved filters:', e)
  }
  return null
}

export function AdminCustomers() {
  const navigate = useNavigate()
  const [customers, setCustomers] = useState<CustomerWithCount[]>([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [sortField, setSortField] = useState<string>('fullName')
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc')

  // Provision partners for filter
  const [provisionPartners, setProvisionPartners] = useState<ProvisionPartner[]>([])

  // Advanced filter state with sessionStorage restoration
  const savedFilters = getSavedFilters()
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(savedFilters?.showAdvancedFilters ?? false)
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>(savedFilters?.selectedStatuses ?? [])
  const [selectedCities, setSelectedCities] = useState<string[]>(savedFilters?.selectedCities ?? [])
  const [selectedPostalCodes, setSelectedPostalCodes] = useState<string[]>(savedFilters?.selectedPostalCodes ?? [])
  const [selectedProvisionPartners, setSelectedProvisionPartners] = useState<string[]>(savedFilters?.selectedProvisionPartners ?? [])
  const [selectedRut, setSelectedRut] = useState<string[]>(savedFilters?.selectedRut ?? [])
  const [itemsPerPage, setItemsPerPage] = useState<number>(savedFilters?.itemsPerPage ?? 20)

  // Search within filters
  const [citySearch, setCitySearch] = useState('')
  const [postalCodeSearch, setPostalCodeSearch] = useState('')

  // Persist filters to sessionStorage
  useEffect(() => {
    const filters: CustomerFilters = {
      showAdvancedFilters,
      selectedStatuses,
      selectedCities,
      selectedPostalCodes,
      selectedProvisionPartners,
      selectedRut,
      itemsPerPage
    }
    sessionStorage.setItem(CUSTOMERS_FILTER_STORAGE_KEY, JSON.stringify(filters))
  }, [showAdvancedFilters, selectedStatuses, selectedCities, selectedPostalCodes, selectedProvisionPartners, selectedRut, itemsPerPage])

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')
    } else {
      setSortField(field)
      setSortDirection('asc')
    }
  }


  // Load customers from Supabase with assignment counts
  const loadCustomers = async () => {
    try {
      setLoading(true)

      // Fetch customers with assignment count
      const { data, error } = await supabase
        .from('customers')
        .select(`
          *,
          assignments:assignments(count)
        `)
        .order('created_at', { ascending: false })

      if (error) {
        console.error('Error loading customers:', error)
        toast.error('Kunde inte ladda kunder: ' + error.message)
        return
      }

      // Transform data to match our CustomerWithCount interface
      const transformedCustomers: CustomerWithCount[] = data?.map(customer => ({
        id: customer.id,
        fullName: customer.full_name,
        email: customer.email,
        phone: customer.phone || '',
        addressStreet: customer.address_street,
        addressCity: customer.address_city,
        addressPostalCode: customer.address_postal_code,
        addressCountry: customer.address_country,
        notes: customer.notes || undefined,
        active: customer.active,
        createdAt: customer.created_at,
        updatedAt: customer.updated_at,
        assignmentCount: customer.assignments?.[0]?.count || 0,
        provisionPartnerId: customer.provision_partner_id,
        fortnoxCustomerId: customer.fortnox_customer_id ?? null,
        wantsRut: customer.wants_rut ?? false
      })) || []

      setCustomers(transformedCustomers)
    } catch (error) {
      console.error('Error loading customers:', error)
      toast.error('Kunde inte ladda kunder')
    } finally {
      setLoading(false)
    }
  }


  // Load provision partners
  const loadProvisionPartners = async () => {
    const { data, error } = await supabase
      .from('provision_partners')
      .select('id, name')
      .eq('is_active', true)
      .order('name')

    if (!error && data) {
      setProvisionPartners(data)
    }
  }

  // Load customers and provision partners on component mount
  useEffect(() => {
    loadCustomers()
    loadProvisionPartners()
  }, [])

  // Extract unique cities from customers
  const uniqueCities = useMemo(() => {
    const cities = customers
      .map(c => c.addressCity)
      .filter(city => city && city.trim() !== '')
    return [...new Set(cities)].sort((a, b) => a.localeCompare(b, 'sv'))
  }, [customers])

  // Filter cities based on search
  const filteredCities = useMemo(() => {
    if (!citySearch) return uniqueCities
    return uniqueCities.filter(city =>
      city.toLowerCase().includes(citySearch.toLowerCase())
    )
  }, [uniqueCities, citySearch])

  // Extract unique postal codes from customers
  const uniquePostalCodes = useMemo(() => {
    const postalCodes = customers
      .map(c => c.addressPostalCode)
      .filter(code => code && code.trim() !== '')
    return [...new Set(postalCodes)].sort((a, b) => a.localeCompare(b, 'sv'))
  }, [customers])

  // Filter postal codes based on search
  const filteredPostalCodes = useMemo(() => {
    if (!postalCodeSearch) return uniquePostalCodes
    return uniquePostalCodes.filter(code =>
      code.toLowerCase().includes(postalCodeSearch.toLowerCase())
    )
  }, [uniquePostalCodes, postalCodeSearch])

  // Status options
  const statusOptions = [
    { key: 'active', name: 'Aktiv' },
    { key: 'inactive', name: 'Inaktiv' }
  ]

  // Count active filters
  const activeFilterCount = [
    selectedStatuses.length > 0,
    selectedCities.length > 0,
    selectedPostalCodes.length > 0,
    selectedProvisionPartners.length > 0,
    selectedRut.length > 0
  ].filter(Boolean).length

  // Clear all filters
  const clearAllFilters = () => {
    setSelectedStatuses([])
    setSelectedCities([])
    setSelectedPostalCodes([])
    setSelectedProvisionPartners([])
    setSelectedRut([])
    setCitySearch('')
    setPostalCodeSearch('')
  }

  // Filter customers based on search term and advanced filters
  const filteredCustomers = useMemo(() => {
    return customers.filter(customer => {
      // Search filter
      const matchesSearch = searchTerm === '' ||
        customer.fullName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        customer.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        customer.phone.includes(searchTerm) ||
        customer.addressCity.toLowerCase().includes(searchTerm.toLowerCase()) ||
        customer.addressPostalCode.includes(searchTerm)

      // Status filter
      const matchesStatus = selectedStatuses.length === 0 ||
        (selectedStatuses.includes('active') && customer.active) ||
        (selectedStatuses.includes('inactive') && !customer.active)

      // City filter
      const matchesCity = selectedCities.length === 0 ||
        selectedCities.includes(customer.addressCity)

      // Postal code filter
      const matchesPostalCode = selectedPostalCodes.length === 0 ||
        selectedPostalCodes.includes(customer.addressPostalCode)

      // Provision partner filter
      const matchesProvisionPartner = selectedProvisionPartners.length === 0 ||
        (selectedProvisionPartners.includes('none') && !customer.provisionPartnerId) ||
        (customer.provisionPartnerId && selectedProvisionPartners.includes(customer.provisionPartnerId))

      // RUT filter
      const matchesRut = selectedRut.length === 0 ||
        (selectedRut.includes('yes') && customer.wantsRut) ||
        (selectedRut.includes('no') && !customer.wantsRut)

      return matchesSearch && matchesStatus && matchesCity && matchesPostalCode && matchesProvisionPartner && matchesRut
    })
  }, [customers, searchTerm, selectedStatuses, selectedCities, selectedPostalCodes, selectedProvisionPartners, selectedRut])

  // Sort customers
  const sortedCustomers = useMemo(() => {
    return [...filteredCustomers].sort((a, b) => {
      let aValue: any = a[sortField as keyof CustomerWithCount]
      let bValue: any = b[sortField as keyof CustomerWithCount]

      if (typeof aValue === 'string') aValue = aValue.toLowerCase()
      if (typeof bValue === 'string') bValue = bValue.toLowerCase()

      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1
      return 0
    })
  }, [filteredCustomers, sortField, sortDirection])

  // Apply items per page limit
  const displayedCustomers = useMemo(() => {
    if (itemsPerPage === 0) return sortedCustomers // 0 = Show all
    return sortedCustomers.slice(0, itemsPerPage)
  }, [sortedCustomers, itemsPerPage])

  // Navigate to customer and store filtered IDs for prev/next navigation
  const navigateToCustomer = (customerId: string) => {
    // Store the current filtered customer IDs for navigation
    const customerIds = sortedCustomers.map(c => c.id)
    sessionStorage.setItem('filtered-customer-ids', JSON.stringify(customerIds))
    navigate(`/admin/customers/${customerId}`)
  }

  // Navigate to assignments filtered by customer
  const navigateToCustomerAssignments = (e: React.MouseEvent, customerId: string) => {
    e.stopPropagation() // Prevent row click
    // Set filter state in sessionStorage for assignments page
    const assignmentFilters = {
      showAdvancedFilters: true,
      dateFrom: '',
      dateTo: '',
      selectedStatuses: [],
      selectedSubcontractors: [],
      selectedCustomers: [customerId],
      selectedCities: []
    }
    sessionStorage.setItem('assignmentFilters', JSON.stringify(assignmentFilters))
    navigate('/admin/assignments')
  }


  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-nature-600 mx-auto"></div>
          <p className="mt-4 text-nature-600 dark:text-nature-400">Laddar kunder...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <h1 className="text-xl sm:text-2xl font-bold text-nature-900 dark:text-nature-100">Kunder</h1>
        <button
          onClick={() => navigate('/admin/customers/new')}
          className="btn-primary flex items-center justify-center space-x-2 w-full sm:w-auto"
        >
          <Plus className="h-4 w-4" />
          <span>Lägg till</span>
        </button>
      </div>

      {/* Search and Filters */}
      <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 sm:items-center">
        {/* Search */}
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
          <input
            type="text"
            placeholder="Sök kunder..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="input-field pl-10 w-full"
          />
        </div>
        {/* Filters row */}
        <div className="flex items-center gap-2">
          {/* Advanced filters toggle */}
          <button
            onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
            className={`flex items-center gap-1.5 px-3 py-2 text-sm border rounded-md transition-colors ${
              activeFilterCount > 0
                ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300'
                : 'border-nature-300 dark:border-nature-600 bg-[#faf9f7] dark:bg-nature-700 text-nature-700 dark:text-nature-200 hover:bg-nature-50 dark:hover:bg-nature-600'
            }`}
          >
            <Filter className="h-4 w-4" />
            <span className="hidden sm:inline">Avancerade filter</span>
            {activeFilterCount > 0 && (
              <span className="px-1.5 py-0.5 text-xs font-medium bg-primary-500 text-white rounded-full">
                {activeFilterCount}
              </span>
            )}
            {showAdvancedFilters ? (
              <ChevronUp className="h-4 w-4" />
            ) : (
              <ChevronDown className="h-4 w-4" />
            )}
          </button>
          <select
            value={itemsPerPage}
            onChange={(e) => setItemsPerPage(Number(e.target.value))}
            className="px-2 py-2 text-sm border border-nature-300 dark:border-nature-600 rounded-md bg-[#faf9f7] dark:bg-nature-700 text-nature-900 dark:text-nature-100"
          >
            <option value={10}>10 st</option>
            <option value={20}>20 st</option>
            <option value={50}>50 st</option>
            <option value={100}>100 st</option>
            <option value={0}>Alla</option>
          </select>
          <span className="text-xs text-nature-500 dark:text-nature-400 whitespace-nowrap">
            ({filteredCustomers.length})
          </span>
        </div>
      </div>

      {/* Advanced Filters Panel */}
      {showAdvancedFilters && (
        <div className="bg-[#faf9f7] dark:bg-nature-800 rounded-lg shadow border border-nature-200 dark:border-nature-700 p-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {/* Status Multi-select */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <label className="block text-sm font-medium text-nature-700 dark:text-nature-300">
                  Status
                  {selectedStatuses.length > 0 && (
                    <span className="ml-1 text-xs text-primary-600">({selectedStatuses.length})</span>
                  )}
                </label>
                {selectedStatuses.length > 0 && (
                  <button
                    onClick={() => setSelectedStatuses([])}
                    className="text-xs text-red-500 hover:text-red-700 dark:text-red-400 flex items-center gap-0.5"
                  >
                    <X className="h-3 w-3" />
                    Rensa
                  </button>
                )}
              </div>
              <div className="max-h-40 overflow-y-auto space-y-1 border border-nature-200 dark:border-nature-600 rounded-md p-2 bg-white dark:bg-nature-700">
                {statusOptions.map(status => (
                  <label key={status.key} className="flex items-center gap-2 cursor-pointer hover:bg-nature-50 dark:hover:bg-nature-600 p-1 rounded">
                    <input
                      type="checkbox"
                      checked={selectedStatuses.includes(status.key)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedStatuses([...selectedStatuses, status.key])
                        } else {
                          setSelectedStatuses(selectedStatuses.filter(s => s !== status.key))
                        }
                      }}
                      className="h-4 w-4 text-primary-600 rounded border-nature-300"
                    />
                    <span className={`text-xs px-1.5 py-0.5 rounded ${
                      status.key === 'active'
                        ? 'bg-green-100 dark:bg-green-800 text-green-800 dark:text-green-200'
                        : 'bg-red-100 dark:bg-red-800 text-red-800 dark:text-red-200'
                    }`}>
                      {status.name}
                    </span>
                  </label>
                ))}
              </div>
            </div>

            {/* City Multi-select */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <label className="block text-sm font-medium text-nature-700 dark:text-nature-300">
                  <MapPin className="inline h-4 w-4 mr-1" />
                  Ort
                  {selectedCities.length > 0 && (
                    <span className="ml-1 text-xs text-primary-600">({selectedCities.length})</span>
                  )}
                </label>
                {selectedCities.length > 0 && (
                  <button
                    onClick={() => { setSelectedCities([]); setCitySearch(''); }}
                    className="text-xs text-red-500 hover:text-red-700 dark:text-red-400 flex items-center gap-0.5"
                  >
                    <X className="h-3 w-3" />
                    Rensa
                  </button>
                )}
              </div>
              <input
                type="text"
                placeholder="Sök..."
                value={citySearch}
                onChange={(e) => setCitySearch(e.target.value)}
                className="input-field text-sm w-full mb-1"
              />
              <div className="max-h-32 overflow-y-auto space-y-1 border border-nature-200 dark:border-nature-600 rounded-md p-2 bg-white dark:bg-nature-700">
                {filteredCities.map(city => (
                  <label key={city} className="flex items-center gap-2 cursor-pointer hover:bg-nature-50 dark:hover:bg-nature-600 p-1 rounded">
                    <input
                      type="checkbox"
                      checked={selectedCities.includes(city)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedCities([...selectedCities, city])
                        } else {
                          setSelectedCities(selectedCities.filter(c => c !== city))
                        }
                      }}
                      className="h-4 w-4 text-primary-600 rounded border-nature-300"
                    />
                    <span className="text-sm text-nature-700 dark:text-nature-200">{city}</span>
                  </label>
                ))}
                {filteredCities.length === 0 && (
                  <p className="text-xs text-nature-500 dark:text-nature-400 italic p-1">Inga orter hittades</p>
                )}
              </div>
            </div>

            {/* Postal Code Multi-select */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <label className="block text-sm font-medium text-nature-700 dark:text-nature-300">
                  Postnummer
                  {selectedPostalCodes.length > 0 && (
                    <span className="ml-1 text-xs text-primary-600">({selectedPostalCodes.length})</span>
                  )}
                </label>
                {selectedPostalCodes.length > 0 && (
                  <button
                    onClick={() => { setSelectedPostalCodes([]); setPostalCodeSearch(''); }}
                    className="text-xs text-red-500 hover:text-red-700 dark:text-red-400 flex items-center gap-0.5"
                  >
                    <X className="h-3 w-3" />
                    Rensa
                  </button>
                )}
              </div>
              <input
                type="text"
                placeholder="Sök..."
                value={postalCodeSearch}
                onChange={(e) => setPostalCodeSearch(e.target.value)}
                className="input-field text-sm w-full mb-1"
              />
              <div className="max-h-32 overflow-y-auto space-y-1 border border-nature-200 dark:border-nature-600 rounded-md p-2 bg-white dark:bg-nature-700">
                {filteredPostalCodes.map(code => (
                  <label key={code} className="flex items-center gap-2 cursor-pointer hover:bg-nature-50 dark:hover:bg-nature-600 p-1 rounded">
                    <input
                      type="checkbox"
                      checked={selectedPostalCodes.includes(code)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedPostalCodes([...selectedPostalCodes, code])
                        } else {
                          setSelectedPostalCodes(selectedPostalCodes.filter(c => c !== code))
                        }
                      }}
                      className="h-4 w-4 text-primary-600 rounded border-nature-300"
                    />
                    <span className="text-sm text-nature-700 dark:text-nature-200">{code}</span>
                  </label>
                ))}
                {filteredPostalCodes.length === 0 && (
                  <p className="text-xs text-nature-500 dark:text-nature-400 italic p-1">Inga postnummer hittades</p>
                )}
              </div>
            </div>

            {/* RUT filter */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <label className="block text-sm font-medium text-nature-700 dark:text-nature-300">
                  RUT-avdrag
                  {selectedRut.length > 0 && (
                    <span className="ml-1 text-xs text-primary-600">({selectedRut.length})</span>
                  )}
                </label>
                {selectedRut.length > 0 && (
                  <button
                    onClick={() => setSelectedRut([])}
                    className="text-xs text-red-500 hover:text-red-700 dark:text-red-400 flex items-center gap-0.5"
                  >
                    <X className="h-3 w-3" />
                    Rensa
                  </button>
                )}
              </div>
              <div className="max-h-40 overflow-y-auto space-y-1 border border-nature-200 dark:border-nature-600 rounded-md p-2 bg-white dark:bg-nature-700">
                <label className="flex items-center gap-2 cursor-pointer hover:bg-nature-50 dark:hover:bg-nature-600 p-1 rounded">
                  <input
                    type="checkbox"
                    checked={selectedRut.includes('yes')}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedRut([...selectedRut, 'yes'])
                      } else {
                        setSelectedRut(selectedRut.filter(r => r !== 'yes'))
                      }
                    }}
                    className="h-4 w-4 text-primary-600 rounded border-nature-300"
                  />
                  <span className="text-xs px-1.5 py-0.5 rounded bg-blue-100 dark:bg-blue-800 text-blue-800 dark:text-blue-200">Ja</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer hover:bg-nature-50 dark:hover:bg-nature-600 p-1 rounded">
                  <input
                    type="checkbox"
                    checked={selectedRut.includes('no')}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedRut([...selectedRut, 'no'])
                      } else {
                        setSelectedRut(selectedRut.filter(r => r !== 'no'))
                      }
                    }}
                    className="h-4 w-4 text-primary-600 rounded border-nature-300"
                  />
                  <span className="text-sm text-nature-700 dark:text-nature-200">Nej</span>
                </label>
              </div>
            </div>

            {/* Provision Partner Multi-select */}
            {provisionPartners.length > 0 && (
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-nature-700 dark:text-nature-300">
                    Provisionspartner
                  </label>
                  {selectedProvisionPartners.length > 0 && (
                    <button
                      onClick={() => setSelectedProvisionPartners([])}
                      className="text-xs text-red-500 hover:text-red-700 dark:text-red-400 flex items-center gap-0.5"
                    >
                      <X className="h-3 w-3" />
                      Rensa
                    </button>
                  )}
                </div>
                <div className="max-h-32 overflow-y-auto space-y-1 border border-nature-200 dark:border-nature-600 rounded-md p-2 bg-white dark:bg-nature-700">
                  <label className="flex items-center gap-2 cursor-pointer hover:bg-nature-50 dark:hover:bg-nature-600 p-1 rounded">
                    <input
                      type="checkbox"
                      checked={selectedProvisionPartners.includes('none')}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedProvisionPartners([...selectedProvisionPartners, 'none'])
                        } else {
                          setSelectedProvisionPartners(selectedProvisionPartners.filter(p => p !== 'none'))
                        }
                      }}
                      className="h-4 w-4 text-primary-600 rounded border-nature-300"
                    />
                    <span className="text-sm text-nature-500 dark:text-nature-400 italic">Ingen partner</span>
                  </label>
                  {provisionPartners.map(partner => (
                    <label key={partner.id} className="flex items-center gap-2 cursor-pointer hover:bg-nature-50 dark:hover:bg-nature-600 p-1 rounded">
                      <input
                        type="checkbox"
                        checked={selectedProvisionPartners.includes(partner.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedProvisionPartners([...selectedProvisionPartners, partner.id])
                          } else {
                            setSelectedProvisionPartners(selectedProvisionPartners.filter(p => p !== partner.id))
                          }
                        }}
                        className="h-4 w-4 text-primary-600 rounded border-nature-300"
                      />
                      <span className="text-sm text-nature-700 dark:text-nature-200">{partner.name}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Clear filters button */}
          {activeFilterCount > 0 && (
            <div className="mt-4 pt-4 border-t border-nature-200 dark:border-nature-600 flex justify-end">
              <button
                onClick={clearAllFilters}
                className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-md transition-colors"
              >
                <X className="h-4 w-4" />
                Rensa alla filter
              </button>
            </div>
          )}
        </div>
      )}

      {/* Customers List */}
      <div className="bg-[#faf9f7] dark:bg-nature-800 rounded-lg shadow border border-nature-200 dark:border-nature-700 overflow-hidden">
        {/* Mobile Card View */}
        <div className="lg:hidden divide-y divide-nature-200 dark:divide-nature-700">
          {displayedCustomers.map((customer) => (
            <div
              key={customer.id}
              onClick={() => navigateToCustomer(customer.id)}
              className="p-4 cursor-pointer active:bg-nature-100 dark:active:bg-nature-700"
            >
              <div className="flex items-center justify-between mb-2">
                <div>
                  <span className="font-medium text-nature-900 dark:text-nature-100">{customer.fullName}</span>
                  <span className={`ml-2 px-2 py-0.5 text-xs rounded-full font-medium ${
                    customer.active
                      ? 'bg-green-100 dark:bg-green-800 text-green-800 dark:text-green-200'
                      : 'bg-red-100 dark:bg-red-800 text-red-800 dark:text-red-200'
                  }`}>
                    {customer.active ? 'Aktiv' : 'Inaktiv'}
                  </span>
                  {customer.wantsRut && (
                    <span className="ml-1 px-2 py-0.5 text-xs rounded-full font-medium bg-blue-100 dark:bg-blue-800 text-blue-800 dark:text-blue-200">
                      RUT
                    </span>
                  )}
                </div>
                {customer.assignmentCount > 0 ? (
                  <button
                    onClick={(e) => navigateToCustomerAssignments(e, customer.id)}
                    className="inline-flex items-center justify-center min-w-[2rem] px-2 py-1 text-sm font-medium text-primary-600 dark:text-primary-400 bg-primary-50 dark:bg-primary-900/30 hover:bg-primary-100 dark:hover:bg-primary-900/50 rounded-full transition-colors"
                    title="Visa uppdrag för denna kund"
                  >
                    {customer.assignmentCount}
                  </button>
                ) : (
                  <span className="text-sm text-nature-400 dark:text-nature-500">0</span>
                )}
              </div>
              <div className="text-sm text-nature-600 dark:text-nature-400 mb-1">
                {customer.addressCity} {customer.addressPostalCode && `(${customer.addressPostalCode})`}
              </div>
              <div className="flex flex-wrap gap-3 text-xs text-nature-500 dark:text-nature-400">
                {customer.phone && (
                  <span className="flex items-center gap-1">
                    <Phone className="h-3 w-3" />
                    {customer.phone}
                  </span>
                )}
                <span className="flex items-center gap-1">
                  <Mail className="h-3 w-3" />
                  {customer.email}
                </span>
                {customer.fortnoxCustomerId && (
                  <span className="text-nature-500 dark:text-nature-400">
                    Fortnox: {customer.fortnoxCustomerId}
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Desktop Table View */}
        <div className="hidden lg:block overflow-x-auto">
          <table className="w-full">
            <thead className="bg-nature-50 dark:bg-nature-700">
              <tr>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-nature-500 dark:text-nature-400 uppercase tracking-wider cursor-pointer hover:bg-nature-100 dark:hover:bg-nature-600"
                  onClick={() => handleSort('fullName')}
                >
                  <div className="flex items-center gap-1">
                    Namn
                    {sortField === 'fullName' ? (
                      sortDirection === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />
                    ) : (
                      <ArrowUpDown className="h-3 w-3 opacity-50" />
                    )}
                  </div>
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-nature-500 dark:text-nature-400 uppercase tracking-wider cursor-pointer hover:bg-nature-100 dark:hover:bg-nature-600"
                  onClick={() => handleSort('email')}
                >
                  <div className="flex items-center gap-1">
                    E-post
                    {sortField === 'email' ? (
                      sortDirection === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />
                    ) : (
                      <ArrowUpDown className="h-3 w-3 opacity-50" />
                    )}
                  </div>
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-nature-500 dark:text-nature-400 uppercase tracking-wider cursor-pointer hover:bg-nature-100 dark:hover:bg-nature-600"
                  onClick={() => handleSort('phone')}
                >
                  <div className="flex items-center gap-1">
                    Telefon
                    {sortField === 'phone' ? (
                      sortDirection === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />
                    ) : (
                      <ArrowUpDown className="h-3 w-3 opacity-50" />
                    )}
                  </div>
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-nature-500 dark:text-nature-400 uppercase tracking-wider cursor-pointer hover:bg-nature-100 dark:hover:bg-nature-600"
                  onClick={() => handleSort('addressCity')}
                >
                  <div className="flex items-center gap-1">
                    Ort
                    {sortField === 'addressCity' ? (
                      sortDirection === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />
                    ) : (
                      <ArrowUpDown className="h-3 w-3 opacity-50" />
                    )}
                  </div>
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-nature-500 dark:text-nature-400 uppercase tracking-wider cursor-pointer hover:bg-nature-100 dark:hover:bg-nature-600"
                  onClick={() => handleSort('addressPostalCode')}
                >
                  <div className="flex items-center gap-1">
                    Postnr
                    {sortField === 'addressPostalCode' ? (
                      sortDirection === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />
                    ) : (
                      <ArrowUpDown className="h-3 w-3 opacity-50" />
                    )}
                  </div>
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-nature-500 dark:text-nature-400 uppercase tracking-wider cursor-pointer hover:bg-nature-100 dark:hover:bg-nature-600"
                  onClick={() => handleSort('fortnoxCustomerId')}
                >
                  <div className="flex items-center gap-1">
                    Fortnox
                    {sortField === 'fortnoxCustomerId' ? (
                      sortDirection === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />
                    ) : (
                      <ArrowUpDown className="h-3 w-3 opacity-50" />
                    )}
                  </div>
                </th>
                <th
                  className="px-6 py-3 text-right text-xs font-medium text-nature-500 dark:text-nature-400 uppercase tracking-wider cursor-pointer hover:bg-nature-100 dark:hover:bg-nature-600"
                  onClick={() => handleSort('assignmentCount')}
                >
                  <div className="flex items-center justify-end gap-1">
                    Uppdrag
                    {sortField === 'assignmentCount' ? (
                      sortDirection === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />
                    ) : (
                      <ArrowUpDown className="h-3 w-3 opacity-50" />
                    )}
                  </div>
                </th>
              </tr>
            </thead>
            <tbody className="bg-[#faf9f7] dark:bg-nature-800 divide-y divide-nature-200 dark:divide-nature-700">
              {displayedCustomers.map((customer) => (
                <tr
                  key={customer.id}
                  onClick={() => navigateToCustomer(customer.id)}
                  className="hover:bg-nature-50 dark:hover:bg-nature-700/50 transition-colors duration-200 cursor-pointer"
                >
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-nature-900 dark:text-nature-100">
                      {customer.fullName}
                    </div>
                    <div className="flex items-center gap-1 mt-1">
                      <span className={`inline-flex px-2 py-0.5 text-xs rounded-full font-medium ${
                        customer.active
                          ? 'bg-green-100 dark:bg-green-800 text-green-800 dark:text-green-200'
                          : 'bg-red-100 dark:bg-red-800 text-red-800 dark:text-red-200'
                      }`}>
                        {customer.active ? 'Aktiv' : 'Inaktiv'}
                      </span>
                      {customer.wantsRut && (
                        <span className="inline-flex px-2 py-0.5 text-xs rounded-full font-medium bg-blue-100 dark:bg-blue-800 text-blue-800 dark:text-blue-200">
                          RUT
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-nature-600 dark:text-nature-400">
                    {customer.email}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-nature-600 dark:text-nature-400">
                    {customer.phone}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-nature-600 dark:text-nature-400">
                    {customer.addressCity}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-nature-600 dark:text-nature-400">
                    {customer.addressPostalCode}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-nature-600 dark:text-nature-400">
                    {customer.fortnoxCustomerId || ''}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right">
                    {customer.assignmentCount > 0 ? (
                      <button
                        onClick={(e) => navigateToCustomerAssignments(e, customer.id)}
                        className="inline-flex items-center justify-center min-w-[2rem] px-2 py-1 text-sm font-medium text-primary-600 dark:text-primary-400 bg-primary-50 dark:bg-primary-900/30 hover:bg-primary-100 dark:hover:bg-primary-900/50 rounded-full transition-colors"
                        title="Visa uppdrag för denna kund"
                      >
                        {customer.assignmentCount}
                      </button>
                    ) : (
                      <span className="text-sm text-nature-400 dark:text-nature-500">0</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {displayedCustomers.length === 0 && !loading && (
        <div className="text-center py-12">
          <User className="h-12 w-12 text-nature-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-nature-900 dark:text-nature-100 mb-2">
            {searchTerm || activeFilterCount > 0 ? 'Inga kunder hittades' : 'Inga kunder än'}
          </h3>
          <p className="text-nature-600 dark:text-nature-400">
            {searchTerm || activeFilterCount > 0 ? 'Prova att ändra söktermen eller filtren' : 'Lägg till din första kund för att komma igång'}
          </p>
          {activeFilterCount > 0 && (
            <button
              onClick={clearAllFilters}
              className="mt-4 text-sm text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300"
            >
              Rensa alla filter
            </button>
          )}
        </div>
      )}


    </div>
  )
}
